import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext(null);

function applyLocale(locale) {
  if (!locale) return;
  const valid = ["fr", "en", "de"];
  if (valid.includes(locale) && i18n.language !== locale) {
    try { i18n.changeLanguage(locale); }
    catch { /* silent — defensive */ }
    try { localStorage.setItem("noam.locale", locale); }
    catch { /* silent — defensive */ }
  }
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      applyLocale(r.data?.locale);
      return r.data;
    } catch {
      // /auth/me 401 is the expected unauthenticated response.
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: retour OAuth Google (#session_id=...) — AuthCallback fait
    // l'échange et établit la session d'abord ; on ne lance pas /auth/me ici.
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);

  // V23.16.3 — Live presence : ping /auth/heartbeat toutes les 60s quand
  // l'utilisateur est authentifié. Permet à Ultra Admin de voir qui est connecté.
  useEffect(() => {
    if (!user || typeof user !== "object") return undefined;
    let cancelled = false;
    const ping = async () => {
      if (cancelled) return;
      try { await api.post("/auth/heartbeat", {}); }
      catch { /* silent — heartbeat non critique */ }
    };
    ping(); // immédiat à la connexion
    const id = setInterval(ping, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    try { sessionStorage.removeItem("noam_welcome_dismissed"); } catch { /* silent */ }
    setUser(r.data);
    applyLocale(r.data?.locale);
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch { /* silent — continue logout anyway */ }
    setUser(false);
  };

  const updatePreferences = async (prefs) => {
    const r = await api.patch("/auth/me/preferences", prefs);
    if (prefs.locale) applyLocale(prefs.locale);
    setUser((u) => (u && typeof u === "object" ? { ...u, ...prefs } : u));
    return r.data;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, updatePreferences }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
